import { useEffect, useState } from 'react';
import { useAuthContext } from '@/hooks/useAuthContext';
import { useCheckUserTosStatusQuery } from '@/redux/api/legal.rtkq';
import TosAcceptanceModal from './TosAcceptanceModal';
import { Spinner } from '@/components/ui/spinner';

interface TosGuardProps {
  children: React.ReactNode;
}

/** Nest 403 when localStorage still points at an office the user left. */
const isOfficeChangedError = (error: unknown): boolean => {
  if (!error || typeof error !== "object" || !("data" in error)) return false;
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== "object") return false;
  const body = data as { code?: unknown; message?: unknown };
  if (body.code === "OFFICE_CHANGED") return true;
  return (
    typeof body.message === "object" &&
    body.message !== null &&
    "code" in body.message &&
    (body.message as { code?: unknown }).code === "OFFICE_CHANGED"
  );
};

export const TosGuard: React.FC<TosGuardProps> = ({ children }) => {
  const { user, loadingUserData, backendReachable } = useAuthContext();
  const { data: tosStatus, isLoading: isTosLoading, error: tosError } = useCheckUserTosStatusQuery(undefined, {
    skip: loadingUserData || !user || !backendReachable,
  });

  const [showTosModal, setShowTosModal] = useState(false);
  const [forceAccept, setForceAccept] = useState(false);

  useEffect(() => {
    // Only show modal if user is loaded and ToS check is complete
    if (!loadingUserData && !isTosLoading && user && tosStatus) {
      if (tosStatus.needsAcceptance) {
        console.log('User needs to accept ToS:', tosStatus);
        // Force acceptance if user has never accepted ToS or if it's required for security reasons
        const shouldForce = !tosStatus.userAcceptance || !tosStatus.userAcceptance.accepted;
        setForceAccept(shouldForce);
        setShowTosModal(true);
      } else {
        setShowTosModal(false);
        setForceAccept(false);
      }
    }
  }, [user, loadingUserData, isTosLoading, tosStatus]);

  // Handle ToS check errors from a reachable backend (new user / missing ToS).
  // Network failures must not force the modal — ProtectedRoutes already shows connectivity UI.
  useEffect(() => {
    if (!loadingUserData && !isTosLoading && user && backendReachable && tosError) {
      const status =
        tosError && typeof tosError === "object" && "status" in tosError
          ? (tosError as { status?: string | number }).status
          : undefined;
      if (status === "FETCH_ERROR" || status === "TIMEOUT_ERROR" || isOfficeChangedError(tosError)) {
        return;
      }
      console.log("Error checking user ToS status:", tosError);
      setForceAccept(true);
      setShowTosModal(true);
    }
  }, [user, loadingUserData, isTosLoading, tosError, backendReachable]);

  const handleTosAccepted = () => {
    setShowTosModal(false);
    setForceAccept(false);
  };

  const handleCloseTosModal = () => {
    if (!forceAccept) {
      setShowTosModal(false);
    }
  };

  // Don't render ToS check for non-authenticated users - let them through to login page
  if (!user) {
    return <>{children}</>;
  }

  if (!backendReachable) {
    return <>{children}</>;
  }

  // CRITICAL: Block all access while loading - show full-screen spinner
  if (loadingUserData || isTosLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Spinner className="size-8" />
          <p className="text-muted-foreground">טוען...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      <TosAcceptanceModal
        isOpen={showTosModal}
        onClose={handleCloseTosModal}
        onAccepted={handleTosAccepted}
        currentVersion={tosStatus?.userAcceptance?.version}
        forceAccept={forceAccept}
      />
    </>
  );
};

export default TosGuard;